import type { ReactNode } from "react";
import {
  PlanYearToggle,
  useDisclosure,
  type CostToolPlanOption,
  type PlanYears,
} from "@dearbill/shared";
import { useApi } from "../../auth/useApi";
import { SearchCombobox } from "./SearchCombobox";
import { RecentChips } from "./RecentChips";
import { SectionHeading, type StepStatus } from "./SectionHeading";

export interface PlanSectionProps {
  orgId: string;
  plan: CostToolPlanOption | null;
  onPlanChange: (plan: CostToolPlanOption | null) => void;
  /** Question 2's status; done once a plan is selected. */
  status: StepStatus;
  /** Selected US state code from question 1, or "" for none; it narrows the plan search. */
  state: string;
  /** The `GET /api/plans/years` payload; null while it loads, which renders no year label. */
  years: PlanYears | null;
  /**
   * The effective plan year: null while `years` carries no next year (no
   * toggle, and the plan search carries no `planYear`), else the toggle's
   * value.
   */
  planYear: number | null;
  onPlanYearChange: (year: number) => void;
  /** Most-recent-first quick-pick plans for the effective year; empty hides the chip row. */
  recentPlans: CostToolPlanOption[];
  /** Wipes both recents lists, for shared pharmacy terminals. */
  onClearRecents: () => void;
}

/** The contract-PBP pair as it reads on a member card ("H1234-001"). */
function contractPbp(plan: CostToolPlanOption): string {
  return `${plan.cmsContractNumber}-${plan.cmsPlanId}`;
}

/**
 * Question 2, the member's plan. Question 1's state narrows the search, then
 * the plan typeahead matches by plan name or a typed contract id like "H1234";
 * the backend handles both. A selected plan collapses to a compact summary
 * with a "Change" button. The "Can't find their plan?" disclosure keeps the
 * pharmacist moving: an uncertain plan degrades the estimate, never the
 * referral, and the refer action stays enabled throughout.
 *
 * Beside the heading, the plan-year control tells the pharmacist which contract
 * year the numbers answer for: the shared `PlanYearToggle` while next year is
 * offered, else a static "<year> plan data" label. The search carries the
 * effective year, so the options are that year's plans.
 *
 * The plans endpoint returns a bare array
 * (`response_model=list[CostToolPlanOption]`); the parser also accepts a
 * `{plans: [...]}` envelope.
 *
 * The recent-plan chips above the search re-select a plan in one tap without a
 * lookup.
 */
export function PlanSection({
  orgId,
  plan,
  onPlanChange,
  status,
  state,
  years,
  planYear,
  onPlanYearChange,
  recentPlans,
  onClearRecents,
}: PlanSectionProps): ReactNode {
  const api = useApi();
  const disclosure = useDisclosure();

  const searchPlans = async (query: string): Promise<CostToolPlanOption[]> => {
    const params = new URLSearchParams();
    params.set("state", state);
    params.set("search", query);
    if (planYear !== null) params.set("planYear", String(planYear));
    const res = await api<{ plans: CostToolPlanOption[] } | CostToolPlanOption[]>(
      `/api/partner/orgs/${orgId}/cost-tool/plans?${params.toString()}`,
    );
    return Array.isArray(res) ? res : res.plans;
  };

  return (
    <section
      aria-labelledby="cost-tool-plan-heading"
      className="border-border-strong rounded-2xl border bg-white p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <SectionHeading id="cost-tool-plan-heading" step={2} status={status}>
            Which drug plan are they on?
          </SectionHeading>
        </div>
        {years !== null && years.next !== null ? (
          <PlanYearToggle
            years={years}
            value={planYear}
            onChange={onPlanYearChange}
            className="shrink-0"
          />
        ) : (
          years !== null && (
            <span className="bg-ice text-slate-teal shrink-0 rounded-md px-2.5 py-1 text-[13px] font-semibold uppercase leading-[18px] tracking-[0.8px]">
              {years.current} plan data
            </span>
          )
        )}
      </div>

      {plan === null && recentPlans.length > 0 && (
        <div className="mt-3">
          <RecentChips
            label="Recent plans"
            items={recentPlans}
            getKey={(p) => p.id}
            getChipLabel={(p) => `Use recent plan ${p.planName}`}
            renderChip={(p) => (
              <span className="flex min-w-0 items-baseline gap-1.5">
                <span className="truncate font-medium">{p.planName}</span>
                <span className="text-slate-teal whitespace-nowrap text-xs">{contractPbp(p)}</span>
              </span>
            )}
            onPick={onPlanChange}
            onClear={onClearRecents}
          />
        </div>
      )}

      {plan ? (
        <div className="border-haze bg-ice mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3">
          <div className="min-w-0">
            <p className="text-foreground truncate text-sm font-semibold">{plan.planName}</p>
            <p className="text-slate-teal truncate text-xs">
              {plan.organizationName} · {contractPbp(plan)}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onPlanChange(null)}
            className="text-ember hover:text-ember-dark cursor-pointer text-sm font-semibold underline underline-offset-2"
          >
            Change
          </button>
        </div>
      ) : (
        <div className="mt-3">
          <p className="text-slate-teal mb-2.5 text-base leading-[22px]">
            The drug plan is printed on the member&rsquo;s plan card.
          </p>
          {/* The question heading above is the visible label; the Figma card
              shows only the question, the helper line and the control. */}
          <SearchCombobox<CostToolPlanOption>
            label="Plan"
            labelHidden
            placeholder="Start typing the plan name"
            listboxLabel="Plan suggestions"
            disabled={!state}
            hint={state ? undefined : "Pick a state first"}
            search={searchPlans}
            onSelect={onPlanChange}
            getKey={(p) => p.id}
            renderItem={(p) => (
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium">{p.planName}</span>
                <span className="text-slate-teal truncate text-xs">
                  {p.organizationName} · {contractPbp(p)}
                </span>
              </span>
            )}
            noMatchesText="No matching plans"
          />
        </div>
      )}

      <div className="mt-3">
        <button
          type="button"
          {...disclosure.triggerProps}
          className="text-slate-teal hover:text-foreground cursor-pointer text-sm underline underline-offset-2"
        >
          Can&rsquo;t find their plan?
        </button>
        {disclosure.open && (
          <div
            {...disclosure.panelProps}
            className="border-haze text-slate-teal mt-2 rounded-md border bg-white px-4 py-3 text-sm"
          >
            <p>
              Look at the member&rsquo;s insurance card for the plan name and a contract ID that
              looks like <span className="text-foreground font-medium">H1234-001</span>.
            </p>
            <p className="mt-2">
              We can&rsquo;t estimate costs without the exact plan — but you can still refer them
              and we&rsquo;ll figure it out together.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
